import { TypedDocumentNode, gql } from "@apollo/client";
import { PageInfo } from "@/components/Pagination";
import { SECTION_FRAGMENT, SectionFragment } from "../queries";
import { PaginationVariables } from "@/components/Pagination";
import { PAGE_INFO_FRAGMENT } from "@/hooks/usePagination";
import { CRM_NOTE_FIELDS_FRAGMENT } from "@/fragments";
import type { CRMNote } from "@/types";

export enum ChangeRequestStatus {
  APPROVED = "APPROVED",
  PENDING = "PENDING",
  REJECTED = "REJECTED",
  CANCELED = "CANCELED"
}

interface Employment {
  effectiveDate: string;
  title: string;
  formattedTitle?: string;
  workingLocation?: string;
}

interface FacilityChange {
  facility: { name: string };
  isPrimaryFacility: boolean;
}

interface Facility {
  name: string;
}

interface CurrentEmployment extends Employment {
  description: string | null;
  classification: string | null;
  facility?: Facility;
  isPrimaryFacility?: boolean;
}

interface AddEmploymentChange extends Employment, FacilityChange {
  classification: string;
  description: string;
  currentEmployment?: CurrentEmployment | null;
  __typename: "BackofficeAddEmploymentChange";
}

interface AddUnlistedEmploymentChange extends Employment, FacilityChange {
  classification?: string;
  description: string;
  currentEmployment?: CurrentEmployment | null;
  __typename: "BackofficeAddUnlistedEmploymentChange";
}

interface UpdateEmploymentChange extends Employment {
  classification: string;
  currentEmployment: CurrentEmployment;
  description: string;
  employmentDeletedAt?: string;
  __typename: "BackofficeUpdateEmploymentChange";
}

interface UpdateUnlistedEmploymentChange extends Employment {
  classification?: string;
  currentEmployment: CurrentEmployment;
  description: string;
  employmentDeletedAt?: string;
  __typename: "BackofficeUpdateUnlistedEmploymentChange";
}

interface LifeMemberChange {
  requestedClassification: string;
  requestedClassificationLabel: string;
  currentClassification?: string | null;
  currentClassificationLabel?: string | null;
  activeDaysAtRequest?: number | null;
  memberAgeAtRequest?: number | null;
  requestedAt?: string | null;
  __typename: "BackofficeLifeMemberChange";
}

export type ChangeRequestChange =
  | AddEmploymentChange
  | AddUnlistedEmploymentChange
  | UpdateEmploymentChange
  | UpdateUnlistedEmploymentChange
  | LifeMemberChange;

type Person = {
  id: string;
  firstName: string;
  lastName: string;
};

export interface Node {
  id: string;
  change: ChangeRequestChange;
  crmNotes: CRMNote[];
  person: { pgaId: string } & Person;
  section: { id: string; name: string };
  status: ChangeRequestStatus;
  submittedAt: string;
}

export const PERSON_FRAGMENT = gql`
  fragment Person on BackofficePerson {
    id
    firstName
    lastName
  }
`;

export const CHANGE_REQUEST_FRAGMENT = gql`
  fragment ChangeRequest on BackofficeChangeRequest {
    id
    status
    section {
      id
      name
    }
    crmNotes {
      ...CRMNoteFields
    }
  }
  ${CRM_NOTE_FIELDS_FRAGMENT}
`;

export const CHANGE_REQUEST_CHANGE_FRAGMENT = gql`
  fragment Change on BackofficeChangeRequest {
    change {
      ... on BackofficeAddUnlistedEmploymentChange {
        ...Employment
        ...FacilityChange
        classification
        description
        currentEmployment {
          ...Employment
          classification
          description
          facility {
            id
            name
          }
          isPrimaryFacility
        }
      }
      ... on BackofficeAddEmploymentChange {
        ...Employment
        ...FacilityChange
        classification
        description
        currentEmployment {
          ...Employment
          classification
          description
          facility {
            id
            name
          }
          isPrimaryFacility
        }
      }
      ... on BackofficeUpdateEmploymentChange {
        ...Employment
        classification
        description
        employmentDeletedAt
        currentEmployment {
          ...Employment
          classification
          description
        }
      }
      ... on BackofficeUpdateUnlistedEmploymentChange {
        ...Employment
        classification
        description
        employmentDeletedAt
        currentEmployment {
          ...Employment
          classification
          description
        }
      }
      ... on BackofficeLifeMemberChange {
        requestedClassification
        requestedClassificationLabel
        currentClassification
        currentClassificationLabel
        activeDaysAtRequest
        memberAgeAtRequest
        requestedAt
      }
    }
  }

  fragment FacilityChange on ChangeRequestEmploymentFacilityChange {
    isPrimaryFacility
    facility {
      id
      name
    }
  }

  fragment Employment on ChangeRequestEmployment {
    id
    title
    formattedTitle
    effectiveDate
    workingLocation
  }
`;

type MutationStatus = { success: boolean; message: string };

export const APPROVE_CHANGE_REQUEST_MUTATION: TypedDocumentNode<
  { approveChangeRequest: MutationStatus },
  { id: string; message?: string }
> = gql`
  mutation ApproveChangeRequest($id: ID!, $message: String) {
    approveChangeRequest(id: $id, message: $message) {
      success
      message
      changeRequest {
        ...ChangeRequest
      }
    }
  }

  ${CHANGE_REQUEST_FRAGMENT}
`;

export const REJECT_CHANGE_REQUEST_MUTATION: TypedDocumentNode<
  { rejectChangeRequest: MutationStatus },
  { id: string; message?: string }
> = gql`
  mutation RejectChangeRequest($id: ID!, $message: String!) {
    rejectChangeRequest(id: $id, message: $message) {
      success
      message
      changeRequest {
        ...ChangeRequest
      }
    }
  }

  ${CHANGE_REQUEST_FRAGMENT}
`;

export const CANCEL_CHANGE_REQUEST_MUTATION: TypedDocumentNode<
  { cancelChangeRequest: MutationStatus },
  { id: string; message: string }
> = gql`
  mutation CancelChangeRequest($id: ID!, $message: String!) {
    cancelChangeRequest(id: $id, message: $message) {
      success
      message
      changeRequest {
        ...ChangeRequest
      }
    }
  }

  ${CHANGE_REQUEST_FRAGMENT}
`;

export interface ChangeRequestStatusChangeEmailPreview {
  subject: string;
  html: string;
}

export interface ChangeRequestStatusChangeEmailPreviewQueryResponse {
  changeRequestStatusChangeEmailPreview: ChangeRequestStatusChangeEmailPreview;
}

export interface ChangeRequestStatusChangeEmailPreviewQueryVariables {
  changeRequestId: string;
  status: ChangeRequestStatus.APPROVED | ChangeRequestStatus.REJECTED;
  messageToMember?: string | null;
}

export interface ChangeRequestReassignEmailPreview {
  subject: string;
  html: string;
}

export interface ChangeRequestReassignEmailPreviewQueryResponse {
  changeRequestReassignEmailPreview: ChangeRequestReassignEmailPreview;
}

export interface ChangeRequestReassignEmailPreviewQueryVariables {
  changeRequestId: string;
  targetSectionId: string;
}

export const CHANGE_REQUEST_REASSIGN_EMAIL_PREVIEW_QUERY: TypedDocumentNode<
  ChangeRequestReassignEmailPreviewQueryResponse,
  ChangeRequestReassignEmailPreviewQueryVariables
> = gql`
  query ChangeRequestReassignEmailPreview($changeRequestId: ID!, $targetSectionId: ID!) {
    changeRequestReassignEmailPreview(changeRequestId: $changeRequestId, targetSectionId: $targetSectionId) {
      subject
      html
    }
  }
`;

export const CHANGE_REQUEST_STATUS_CHANGE_EMAIL_PREVIEW_QUERY: TypedDocumentNode<
  ChangeRequestStatusChangeEmailPreviewQueryResponse,
  ChangeRequestStatusChangeEmailPreviewQueryVariables
> = gql`
  query ChangeRequestStatusChangeEmailPreview(
    $changeRequestId: ID!
    $status: BackofficeChangeRequestStatus!
    $messageToMember: String
  ) {
    changeRequestStatusChangeEmailPreview(
      changeRequestId: $changeRequestId
      status: $status
      messageToMember: $messageToMember
    ) {
      subject
      html
    }
  }
`;

export const UPDATE_CHANGE_REQUEST_MUTATION: TypedDocumentNode<
  { updateChangeRequest: MutationStatus },
  { id: string; classification?: string }
> = gql`
  mutation UpdateChangeRequest($id: ID!, $classification: String) {
    updateChangeRequest(id: $id, classification: $classification) {
      success
      message
      changeRequest {
        id
        ...Change
      }
    }
  }

  ${CHANGE_REQUEST_CHANGE_FRAGMENT}
`;

export const CHANGE_REQUESTS_QUERY: TypedDocumentNode<
  {
    section: SectionFragment["section"] & {
      changeRequests: { nodes: (Node | null)[]; pageInfo: PageInfo; totalCount: number };
    };
  },
  { sectionId: string; query?: string; stage?: ChangeRequestStatus } & PaginationVariables
> = gql`
  query SectionChangeRequests(
    $sectionId: ID!
    $query: String
    $stage: BackofficeChangeRequestStatus
    $first: Int
    $after: String
    $last: Int
    $before: String
  ) {
    section(id: $sectionId) {
      ...SectionFragment
      changeRequests(query: $query, stage: $stage, first: $first, after: $after, last: $last, before: $before) {
        nodes {
          ...ChangeRequest
          submittedAt
          ...Change
          person {
            pgaId
            ...Person
          }
        }
        ...PageInfo
      }
    }
  }

  ${SECTION_FRAGMENT}
  ${PERSON_FRAGMENT}
  ${CHANGE_REQUEST_FRAGMENT}
  ${CHANGE_REQUEST_CHANGE_FRAGMENT}
  ${PAGE_INFO_FRAGMENT}
`;

export const JOB_CLASSIFICATIONS_QUERY: TypedDocumentNode<
  { person: { availableJobClassifications: string[] } },
  { personId: string }
> = gql`
  query JobClassifications($personId: ID!) {
    person(id: $personId) {
      id
      availableJobClassifications
    }
  }
`;

export type ReassignableSection = { id: string; name: string };

export const REASSIGN_SECTIONS_QUERY: TypedDocumentNode<{ sections: ReassignableSection[] }> = gql`
  query ReassignableSections {
    sections {
      id
      name
    }
  }
`;

export const REASSIGN_CHANGE_REQUEST_MUTATION: TypedDocumentNode<
  { reassignChangeRequest: MutationStatus },
  { id: string; targetSectionId: string; message?: string }
> = gql`
  mutation ReassignChangeRequest($id: ID!, $targetSectionId: ID!, $message: String) {
    reassignChangeRequest(id: $id, targetSectionId: $targetSectionId, message: $message) {
      success
      message
      changeRequest {
        ...ChangeRequest
      }
    }
  }

  ${CHANGE_REQUEST_FRAGMENT}
`;
